import cloneDeep from 'lodash/cloneDeep';
import keyBy from 'lodash/keyBy';
import orderBy from 'lodash/orderBy';

import { EntryTypeFieldType, Locale, DictionaryNames } from 'chaika-enums';
import { EhrData, EntryTypeFieldName, ReferralStatus, UserData } from 'chaika-frontend-types/frontend-cms';

import { getTranslate, translate } from '@chaika/i18n/helper';
import {
    EntryIndividualReportData,
    EntryIndividualReportEntryParams,
    EntryIndividualReportDiagnosesParams,
    EntryIndividualReportTranslationParams,
    EntryIndividualReportParams,
} from '@patient/services/entry/types';
import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { userQueryOptions } from '@user/queries/userQueries';
import { getFullName } from '@user/utils/getFullName';
import { DateFormat, DateHelper } from '@utils/DateHelper';
import { isDefined } from '@utils/isDefined';
import { getEnumValues } from '@utils/getEnumValues';
import { assertIsDefined } from '@utils/assertIsDefined';
import { ReferralField } from '@store/models/EntryFields';
import { outwardReferralToItemParams, referralToItemParams } from '@common/TreatmentPlan/utils/treatmentPlanData';
import { isOutwardReferralOfEntry } from '@common/TreatmentPlan/utils/outwardReferralFilter';
import { ItemType } from '@common/TreatmentPlan/types';

class EntryIndividualPrintFormService {
    public async makeIndividualReportData(ehrData?: EhrData): Promise<EntryIndividualReportData> {
        const { ehr, entry } = store;

        const [doctor] = isDefined(entry.doctorId)
            ? await queryClient.fetchQuery(userQueryOptions.list({ ids: [entry.doctorId] }))
            : [];

        return {
            currentDate: DateHelper.now().toISOString(),
            currentDateSting: DateHelper.format(new Date(), DateFormat.DateWithDotSeparator),
            ehr: ehrData
                ? {
                      ...ehrData,
                      birthDate: DateHelper.format(ehrData.birthDate, DateFormat.DateWithDotSeparator),
                      identityDocDate: DateHelper.format(ehrData.identityDocDate, DateFormat.DateWithDotSeparator),
                      internationalIdIssueDate: DateHelper.format(
                          ehrData.internationalIdIssueDate,
                          DateFormat.DateWithDotSeparator,
                      ),
                      internationalIdExpiryDate: DateHelper.format(
                          ehrData.internationalIdExpiryDate,
                          DateFormat.DateWithDotSeparator,
                      ),
                      translations: keyBy(ehrData.translations, 'locale'),
                  }
                : {
                      ...ehr.serialize(),
                      birthDate: ehr.birthDate ?? '',
                      translations: Object.fromEntries(ehr.translations),
                  },
            entry: this.makeEntryIndividualReportData(doctor),
        };
    }

    private makeEntryIndividualReportData(doctor: UserData | undefined): EntryIndividualReportEntryParams {
        const { entry, organizationList } = store;
        const { ehrFields, fields, entryType } = entry;
        assertIsDefined(entry.status, 'Printed entry is saved, so it has a status');

        return {
            id: entry.id,
            author: entry.author,
            serialNumber: entry.serialNumber,
            authorId: entry.authorId,
            doctorId: entry.doctorId,
            entryDiagnoses: this.getEntryDiagnoses(),
            episodesDrugs: this.getMedications(),
            episodesRecommendations: this.getRecommendations(),
            episodesReferrals: this.getReferrals(),
            doctor: doctor && getFullName(doctor),
            creationTime: entry.creationTime,
            creationTimeString: DateHelper.format(entry.creationTime, DateFormat.DateWithDotSeparator),
            signedAt: entry.signedAt,
            signedAtString: isDefined(entry.signedAt)
                ? DateHelper.format(entry.signedAt, DateFormat.DateWithDotSeparator)
                : undefined,
            visitTime: entry.visitTime ?? entry.creationTime,
            visitTimeString: DateHelper.format(entry.visitTime ?? entry.creationTime, DateFormat.DateWithDotSeparator),
            note: entry.note,
            organizationId: entry.organizationId,
            organization: organizationList.getExisting(entry.organizationId).name,
            status: entry.status,
            typeId: entry.typeId,
            updateTime: entry.updateTime,
            ehrFields: ehrFields.fieldsPreviewWithName,
            fields: fields.fieldsPreviewWithName,
            ehrFieldObject: ehrFields.fieldsPreviewWithNameObject,
            fieldObject: fields.fieldsPreviewWithNameObject,
            entryType: {
                id: entryType.id,
                title: entryType.title,
                type: entryType.type,
            },
        };
    }

    private getEntryDiagnoses(): EntryIndividualReportDiagnosesParams[] {
        const { entry, dictionary } = store;
        const icdDictionary = dictionary.getDictionary(DictionaryNames.Mkb10);
        const translationIcdDictionaries = getEnumValues(Locale).map(locale => ({
            locale,
            dictionary: dictionary.getTranslatedDictionary(locale, DictionaryNames.Mkb10),
        }));
        const diagnosis = entry.medicalCases.getAllDiagnoses();

        return diagnosis
            .filter(({ parentEntryId }) => parentEntryId === entry.id)
            .map(diagnoses => {
                const icd = icdDictionary.get(diagnoses.icdId);
                const icdValue = icd?.value ?? '';
                const icdTitle = icd?.title ?? '';

                const translations: Record<string, EntryIndividualReportTranslationParams> = {};

                if (icdValue) {
                    translationIcdDictionaries.forEach(({ locale, dictionary }) => {
                        const translatedIcd = dictionary?.get(diagnoses.icdId);

                        if (translatedIcd) {
                            translations[locale] = {
                                locale,
                                icdValue: translatedIcd.value ?? '',
                                icdTitle: translatedIcd.title ?? '',
                                icdCode: `${translatedIcd.value} ${translatedIcd.title}`,
                            };
                        }
                    });
                }

                return {
                    icdValue,
                    icdTitle,
                    translations,
                    icdCode: icd ? `${icdValue} ${icdTitle}` : '',
                    description: diagnoses.clinicalDiagnosis,
                };
            });
    }

    private getReferrals(): EntryIndividualReportParams[] {
        const outwardReferrals = store.entry.medicalCases
            .getAllOutwardReferrals()
            .filter(it => isOutwardReferralOfEntry(it, store.entry.id));
        const referralField: ReferralField = store.entry.getField(EntryTypeFieldType.Referrals) as ReferralField;
        const referralList = referralField.getValue();

        const entryReferrals = referralList.values.filter(
            ({ parentEntryId, tiedEntryIds = [], status }) =>
                status !== ReferralStatus.Cancelled &&
                (parentEntryId === store.entry.id ||
                    (tiedEntryIds.includes(store.entry.id) && status !== ReferralStatus.Executed)),
        );

        const ordered = orderBy(
            [
                ...entryReferrals.map(referral => referralToItemParams(referral)),
                ...outwardReferrals.map(outwardReferral => outwardReferralToItemParams(outwardReferral)),
            ],
            'startDate',
        );

        const getOutwardReferralValue = (id: string, locale?: Locale): string => {
            const outwardReferral = store.entry.medicalCases.getOutwardReferral(id);

            if (!outwardReferral) {
                return '-';
            }

            const title = `${outwardReferral.name} ${outwardReferral.comment ?? ''}`;

            return outwardReferral.executionEntryId === store.entry.id
                ? `${title} (${getTranslate('performed', locale)})`
                : title;
        };

        return ordered.map(item => {
            const translations: Record<string, any> = {};

            Object.values(Locale).forEach(locale => {
                const title = !!item.endDate
                    ? `${getTranslate('to', locale)} ${DateHelper.format(item.endDate, DateFormat.DayWithMonthString)}`
                    : getTranslate('indefiniteFem', locale);

                const value =
                    item.type === ItemType.Referral
                        ? referralField.getPrintItemTitle(item.id, locale)
                        : getOutwardReferralValue(item.id, locale);

                translations[locale] = {
                    title,
                    value,
                };
            });

            return {
                title: !!item.endDate
                    ? `${getTranslate('to')} ${DateHelper.format(item.endDate, DateFormat.DayWithMonthString)}`
                    : getTranslate('indefiniteFem'),
                value:
                    item.type === ItemType.Referral
                        ? referralField.getPrintItemTitle(item.id)
                        : getOutwardReferralValue(item.id),
                translations,
            };
        });
    }

    private getRecommendations(): EntryIndividualReportParams[] {
        const entryRecommendations = store.entry.medicalCases.getRecommendations().filter(({ parentEntryId, end }) => {
            const isOfThisEntry = parentEntryId === store.entry.id;
            const isExpired = end ? DateHelper.isBefore(end, DateHelper.getEndOfDay(Date.now())) : false;

            return isOfThisEntry && !isExpired;
        });

        return entryRecommendations.map(({ id, end, comment: originalComment }) => {
            const translations: Record<string, any> = {};

            let comment = originalComment;

            store.entry.translations.forEach(translation => {
                const translatedRecommendations = translation.fieldList.get(
                    EntryTypeFieldType.EpisdodesRecommendations,
                );

                if (translatedRecommendations) {
                    translations[translation.locale] = {
                        title: end
                            ? `${getTranslate('to', translation.locale)} ${DateHelper.format(
                                  end,
                                  DateFormat.DayWithMonthString,
                                  undefined,
                                  translation.locale,
                              )}`
                            : getTranslate('indefiniteFem', translation.locale),
                        value: translatedRecommendations.value.find((it: { id: string }) => it.id === id)?.comment,
                    };
                }
            });

            if (store.entry.selectedLang) {
                const recommendationTranslations = store.entry.translations
                    .getExisting(store.entry.selectedLang)
                    .fieldList.get(EntryTypeFieldName.EpisdodesRecommendations);

                comment =
                    recommendationTranslations?.value.find((it: { id: string }) => it.id === id)?.comment ??
                    originalComment;
            }

            return {
                title: end
                    ? `${translate('to')} ${DateHelper.format(end, DateFormat.DayWithMonthString)}`
                    : translate('indefiniteFem'),
                value: comment,
                translations,
            };
        });
    }

    private getMedications(): any {
        const {
            drugList,
            medicalCases,
            creationTime,
            signedAt,
            id: entryId,
            isSigned,
            drugHistoryList,
            selectedLang,
        } = store.entry;
        let drugs = medicalCases.drugsIds
            .map(id => {
                const clonedDrug = cloneDeep(drugList.getExisting(id));

                if (selectedLang) {
                    const drugTranslations = store.entry.translations
                        .getExisting(selectedLang)
                        .fieldList.get(EntryTypeFieldName.EpisdodesDrugs);

                    clonedDrug.frequencyIfReception = drugTranslations?.value.find(
                        (it: { id: string }) => it.id === id,
                    )?.frequencyIfReception;
                }

                return clonedDrug;
            })
            .filter(drug => drug.isActualOnDate(signedAt ?? creationTime) || drug.isBelongToEntry(entryId));

        if (isSigned) {
            drugs = drugs.filter(drug => {
                const drugHistory = drugHistoryList.get(drug.id);
                return drugHistory?.metaData?.some(({ entryId: metaEntryId }) => metaEntryId === entryId);
            });
        }

        return drugs.map(drug => {
            const translations: Record<string, any> = {};

            Object.values(Locale).forEach(locale => {
                const translation = store.entry.translations.getExisting(locale);
                const translatedDrugs = translation.fieldList.get(EntryTypeFieldType.EpisdodesDrugs);

                if (translatedDrugs) {
                    const frequencyIfReception = translatedDrugs.value.find(
                        (it: { id: string }) => it.id === drug.id,
                    ).frequencyIfReception;

                    translations[translation.locale] = {
                        title: drug.getEndString(translation.locale),
                        value: drug.getCustomDescriptionForTranslations(frequencyIfReception, translation.locale),
                    };
                }
            });

            return {
                title: drug.getEndString(selectedLang),
                value: drug.description,
                translations,
            };
        });
    }
}

export const entryIndividualPrintFormService = new EntryIndividualPrintFormService();
